// VoyageursScreen.tsx
import React, { useEffect, useState } from 'react';

import { useNavigate } from '../i18n/router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Avatar,
  Chip,
  Fade,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import Header from '../components/Header';
import CardSkeleton from '../components/CardSkeleton';
import { recupererListeVoyageurs, supprimerVoyageurService } from '../Services';
import { Voyageurs } from '../models/Voyageurs';

const VoyageursScreen: React.FC = () => {
  const { t } = useTranslation('screens');
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const [voyageurs, setVoyageurs] = useState<Voyageurs[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchVoyageurs = async () => {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson);
      const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
      const updatedVoyageurs = listeVoyageurs.map((voyageur: Voyageurs) => ({
        ...voyageur,
        url: voyageur.url ? `${voyageur.url}?time=${Date.now()}` : '',
      }));
      setVoyageurs(updatedVoyageurs);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchVoyageurs();
  }, []);

  const handleDelete = (id: number, nom: string, prenom: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const message = t('voyageurs.deleteConfirm', { nom, prenom });
    if (window.confirm(message)) {
      supprimerVoyageurService(id)
        .then(() => fetchVoyageurs())
        .catch((error) => console.error('Erreur lors de la suppression du voyageur', error));
    }
  };

  const isVoyageurListEmpty = !isLoading && voyageurs.length === 0;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: 'linear-gradient(180deg, #FFF8EC 0%, #FAF3E2 100%)',
        backgroundColor: 'transparent',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        overflowY: 'auto',
      }}
    >
      <Header />

      <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
        {/* Page Title (desktop) */}
        {isDesktop && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: '#1A1A1A', fontFamily: "'Fraunces', serif" }}
            >
              {t('voyageurs.title')}
            </Typography>
            <Typography variant="body1" sx={{ color: '#4A4A4A', mt: 0.5 }}>
              {t('voyageurs.subtitle')}
            </Typography>
          </Box>
        )}

        {/* Empty state */}
        {isVoyageurListEmpty && (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              px: 3,
              backgroundColor: 'rgba(255,255,255,0.9)',
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <PersonIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
              {t('voyageurs.empty.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
              {t('voyageurs.empty.subtitle')}
            </Typography>
          </Box>
        )}

        {/* Create button */}
        <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' }, mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/formulaire_creation_voyageur/')}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              py: 1.5,
              px: { xs: 3, sm: 4 },
              borderRadius: 3,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #D89D2F 0%, #B5811F 100%)',
              boxShadow: '0 4px 14px rgba(46,125,50,0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #B5811F 0%, #8C6418 100%)',
                boxShadow: '0 6px 20px rgba(46,125,50,0.4)',
              },
            }}
          >
            {t('voyageurs.createBtn')}
          </Button>
        </Box>

        {/* Skeleton Loading */}
        {isLoading && <CardSkeleton count={3} variant="voyageur" />}

        {/* Voyageurs Grid */}
        <Grid
          container
          spacing={2}
          justifyContent={voyageurs.length === 1 ? 'center' : 'flex-start'}
        >
          {voyageurs.map((item, index) => (
            <Grid
              item
              key={index}
              xs={12}
              sm={voyageurs.length === 1 ? 6 : 6}
              md={voyageurs.length === 1 ? 4 : 4}
              lg={voyageurs.length <= 2 ? 4 : 3}
            >
              <Fade in={true} timeout={Math.min(300 + index * 100, 800)}>
              <Card
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: isDesktop ? 'translateY(-4px)' : 'none',
                    boxShadow: isDesktop
                      ? '0 8px 30px rgba(0,0,0,0.15)'
                      : '0 2px 12px rgba(0,0,0,0.08)',
                  },
                }}
                onClick={() =>
                  navigate(`/voyageur_update/${item.id}`, { state: { voyageur: item } })
                }
              >
                {/* Top accent bar */}
                <Box
                  sx={{
                    height: 4,
                    background: 'linear-gradient(90deg, #D89D2F, #F5C770)',
                  }}
                />

                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  {/* Avatar */}
                  <Avatar
                    src={item.url || '/assets/avatar.png'}
                    alt={`${item.nom} ${item.prenom}`}
                    sx={{
                      width: { xs: 90, sm: 100, md: 110 },
                      height: { xs: 90, sm: 100, md: 110 },
                      mx: 'auto',
                      mb: 2,
                      boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                      border: '3px solid white',
                    }}
                  />

                  {/* Name */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      color: '#333',
                      lineHeight: 1.3,
                    }}
                  >
                    {item.prenom} {item.nom}
                  </Typography>

                  {/* Passport number */}
                  {item.numeropax && (
                    <Chip
                      label={item.numeropax}
                      size="small"
                      variant="outlined"
                      sx={{
                        mt: 1,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        borderColor: '#ddd',
                        color: '#666',
                      }}
                    />
                  )}

                  {/* Info chips */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 1,
                      mt: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    {item.profession && (
                      <Chip
                        label={item.profession}
                        size="small"
                        sx={{
                          backgroundColor: '#e8f5e9',
                          color: '#2e7d32',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                        }}
                      />
                    )}
                    {item.ville && (
                      <Chip
                        label={item.ville}
                        size="small"
                        sx={{
                          backgroundColor: '#e3f2fd',
                          color: '#B5811F',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                        }}
                      />
                    )}
                  </Box>

                </CardContent>

                {/* Action buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2,
                    pb: 2,
                  }}
                >
                  <IconButton
                    size="small"
                    sx={{
                      color: '#2e7d32',
                      backgroundColor: '#e8f5e9',
                      '&:hover': { backgroundColor: '#c8e6c9' },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/voyageur_update/${item.id}`, { state: { voyageur: item } });
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={(e) => handleDelete(item.id, item.nom, item.prenom, e)}
                    sx={{
                      color: '#e53935',
                      backgroundColor: '#ffebee',
                      '&:hover': { backgroundColor: '#ffcdd2' },
                    }}
                    aria-label="delete"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default VoyageursScreen;
